import { useContext, useEffect, useState } from 'react'

import {
  Box,
  Button,
  CheckBox,
  Icon,
  Input,
  MonthPicker,
  Table,
  TableHeader,
  TablePagination,
  Text,
} from '@fairfashion/design-system'
import { useNavigate } from 'react-router-dom'

import { ItemResponseDTO } from '../../../../sdk/items/items.types'
import { ProviderItemsContext } from '../../provider-items.context'
import { UploadToIaModal } from '../UploadToIAModal/upload-to-ia-modal.comp'

import * as Styles from './pending-items-tab.styles'

export const PendingItemsTab = () => {
  const [filter, setFilter] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedItems, setSelectedItems] = useState<ItemResponseDTO[]>([])
  const [iaActionsModalOpen, setIAActionsModalOpen] = useState(false)
  const [startMonth, setStartMonth] = useState<Date | undefined>(undefined)
  const [endMonth, setEndMonth] = useState<Date | undefined>(undefined)

  const { pendingItems, pendingItemsCount, actions } =
    useContext(ProviderItemsContext)

  const navigate = useNavigate()

  const fetchPendingItems = () => {
    if (startMonth && endMonth) {
      actions.fetchPendingItems(
        page,
        itemsPerPage,
        filter,
        startMonth,
        endMonth,
      )
    } else {
      actions.fetchPendingItems(page, itemsPerPage, filter)
    }
  }

  useEffect(() => {
    fetchPendingItems()
  }, [page, itemsPerPage])

  useEffect(() => {
    if (filter.length === 0) {
      fetchPendingItems()
    }
  }, [filter])

  const allPageSelected =
    pendingItems.length > 0 &&
    pendingItems.every((item) => selectedItems.some((i) => i.id === item.id))

  const handleClickCard = (itemId: string) => {
    navigate(`/origins/${itemId}`)
  }

  const handleSelectItem = (item: ItemResponseDTO) => {
    setSelectedItems((selectedItems) =>
      selectedItems.some((i) => i.id === item.id)
        ? selectedItems.filter((i) => i.id !== item.id)
        : [...selectedItems, item],
    )
  }

  const handleAllPageSelected = () => {
    if (allPageSelected) {
      setSelectedItems(
        selectedItems.filter(
          (item) => !pendingItems.some((i) => i.id === item.id),
        ),
      )
    } else {
      const filtered = selectedItems.filter(
        (item) => !pendingItems.some((i) => i.id === item.id),
      )
      setSelectedItems([...filtered, ...pendingItems])
    }
  }

  const handleOpenIAActionsModal = () => {
    setIAActionsModalOpen(true)
  }

  const handleCloseIAActionsModal = () => {
    setIAActionsModalOpen(false)
  }

  const tableHeaders: TableHeader[] = [
    {
      key: 'select',
      label: 'Selecionar',
      width: '100px',
      align: 'center',
      customRender: (item) => (
        <Box width="100%" justifyContent="center">
          <Box width="20px">
            <CheckBox
              size={20}
              checked={selectedItems.some((i) => i.id === item.id)}
              onChange={() => handleSelectItem(item)}
            />
          </Box>
        </Box>
      ),
    },
    {
      key: 'code',
      label: 'Código do Item',
      minWidth: '200px',
      width: '20%',
    },
    {
      key: 'client.name',
      label: 'Cliente',
      minWidth: '300px',
      width: '40%',
    },
    {
      key: 'invoice.code',
      label: 'Número da NF',
      minWidth: '150px',
      width: '10%',
    },
    {
      key: 'invoice.createDate',
      label: 'Data',
      minWidth: '100px',
      width: '10%',
      align: 'center',
      customRender: (item) => (
        <Box width="100%" justifyContent="center">
          <Text size="small">
            {new Date(item.createdAt).toLocaleDateString()}
          </Text>
        </Box>
      ),
    },
    {
      key: 'actions',
      label: 'Ações',
      width: '20%px',
      align: 'center',
      customRender: (item) => (
        <Box width="100%" justifyContent="center">
          <Button
            padding="10px 14px"
            variant="outlined"
            fontSize="15px"
            onClick={() => handleClickCard(item.id)}
          >
            <Icon name="add" color="grey_1" size={20} />
            Cadastrar
          </Button>
        </Box>
      ),
    },
  ]

  return (
    <Box direction="column" width="100%">
      <Box width="100%" gap={2}>
        <Box
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Box flex={1} gap={2} alignItems="center">
            <Input
              placeholder="Busque pelo item, nota fiscal ou cliente"
              adornment={<Icon name="search" color="grey_2" />}
              adornmentPosition="left"
              width="400px"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />

            <Box width="150px">
              <MonthPicker
                label="De"
                onChange={(month, year) => {
                  if (typeof month === 'number' && typeof year === 'number') {
                    setStartMonth(new Date(year, month))
                  } else {
                    setStartMonth(undefined)
                  }
                }}
              />
            </Box>

            <Box width="150px">
              <MonthPicker
                label="Até"
                onChange={(month, year) => {
                  if (typeof month === 'number' && typeof year === 'number') {
                    setEndMonth(new Date(year, month))
                  } else {
                    setEndMonth(undefined)
                  }
                }}
              />
            </Box>

            <Button onClick={fetchPendingItems}>Pesquisar</Button>
          </Box>

          <Styles.IAButton
            padding="14px 16px"
            onClick={handleOpenIAActionsModal}
            disabled={selectedItems.length === 0}
          >
            <Icon name="sparkling" color="white" size={20} />
            Enviar Origens por IA ({selectedItems.length})
          </Styles.IAButton>
        </Box>
      </Box>

      <Box marginTop={3} width="100%" direction="column">
        {pendingItemsCount > 0 ? (
          <>
            <Box
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              paddingY={2}
              paddingX={1}
              backgroundColor="grey_6"
            >
              <Box alignItems="center" gap={2}>
                <CheckBox
                  size={20}
                  checked={allPageSelected}
                  onChange={handleAllPageSelected}
                  label="Selecionar todos os itens desta página"
                />
              </Box>

              <Box alignItems="center" gap={2}>
                <Text size="small" color="grey_2">
                  {selectedItems.length} item(s) selecionado(s) de{' '}
                  {pendingItemsCount} item(s) pendente(s) no total
                </Text>
              </Box>
            </Box>

            <Box
              width="100%"
              direction="column"
              style={{
                height: '1000px',
                overflow: 'hidden',
              }}
            >
              <Box
                style={{
                  height: 'calc(100% - 345px)',
                  overflow: 'auto',
                  width: '100%',
                }}
              >
                <Table
                  spacing="small"
                  headers={tableHeaders}
                  data={pendingItems}
                />
              </Box>

              <Box
                width="100%"
                justifyContent="center"
                paddingY={2}
                style={{
                  borderTop: '1px solid #e0e0e0',
                }}
              >
                <TablePagination
                  total={pendingItemsCount}
                  current={page}
                  onChange={(_page) => setPage(_page)}
                  itemsPerPage={itemsPerPage}
                  onChangeItemsPerPage={(_itemsPerPage) =>
                    setItemsPerPage(_itemsPerPage)
                  }
                />
              </Box>
            </Box>
          </>
        ) : (
          <Box
            height="100px"
            width="100%"
            justifyContent="center"
            alignItems="center"
          >
            <Text color="grey_3">Nenhum item pendente encontrado</Text>
          </Box>
        )}
      </Box>

      <UploadToIaModal
        open={iaActionsModalOpen}
        onClose={handleCloseIAActionsModal}
        selectedItems={selectedItems}
      />
    </Box>
  )
}
